// ============================================================
// ONBOARDING — 初回オンボーディングガイドの既読管理（ユーザー単位・端末をまたぐ）。
//
// 「このユーザーが一度でもガイドを完了/スキップしたか」を Firestore
// `userPrefs/{uid}`（doc id = Auth uid）に保存する。これにより：
//   • 初回ログイン時のみ自動でガイドを表示する（既読なら出さない）
//   • 端末をまたいでも「1人につき1回だけ」自動表示される（旧 localStorage は
//     端末ごとだったため、別端末で再表示されてしまっていた）
//   • 「ガイド」導線からはいつでも手動で再表示できる（既読フラグは触らない）
//
// localStorage は **ちらつき防止キャッシュ** として併用する：
//   • Firestore 読取は非同期なので、初回描画時にガイドが一瞬出てしまうのを防ぐため
//     既読キャッシュ（localStorage）を先に見て、既読なら Firestore を待たずに非表示。
//   • markOnboarded 時にも localStorage に書いて、同一端末の次回をキャッシュで即決。
//
// db 未設定（DEV/Firebase 未構成）時は Firestore を触らず localStorage だけで
// 従来どおり動く（既存挙動を壊さない・落とさない）。
// ============================================================
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from './firebase';

/** uid 単位の「ガイド既読」localStorage キャッシュキー。 */
export function onboardedKey(uid: string): string {
  return `tp:onboarded:${uid}`;
}

/** localStorage の既読キャッシュを読む（SSR/localStorage 不可時は false）。 */
function hasOnboardedCache(uid: string): boolean {
  try {
    if (typeof localStorage === 'undefined') return false;
    return localStorage.getItem(onboardedKey(uid)) === '1';
  } catch {
    return false;
  }
}

/** localStorage の既読キャッシュを書く。失敗は握りつぶす。 */
function writeOnboardedCache(uid: string): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(onboardedKey(uid), '1');
    }
  } catch {
    /* localStorage 不可環境ではキャッシュが残らない＝Firestore を毎回読むだけ・安全側 */
  }
}

/**
 * このユーザー（uid）が既にガイドを完了/スキップしたか。
 *
 * 1. localStorage キャッシュが既読なら true（Firestore を待たずに即決・ちらつき防止）。
 * 2. db 未設定（DEV/Firebase 未構成）なら localStorage のみで判定（従来挙動）。
 * 3. Firestore `userPrefs/{uid}` を読み、onboardedAt があれば既読
 *    （ついでに localStorage キャッシュを温める）。
 *
 * 未保存（初回）/未ログイン（uid空）/読取失敗時は false（＝初回表示する）。
 * Firestore 読取失敗時も localStorage キャッシュにフォールバックして落とさない。
 */
export async function hasOnboarded(uid: string): Promise<boolean> {
  if (!uid) return false;
  // 1. キャッシュ既読なら即 true（非同期待ちのちらつきを防ぐ）。
  if (hasOnboardedCache(uid)) return true;
  // 2. Firestore 未設定なら localStorage だけで判定（DEV/未構成は従来どおり）。
  if (!db) return false;
  // 3. Firestore を本ソースとして読む。
  try {
    const snap = await getDoc(doc(db, 'userPrefs', uid));
    const onboarded = snap.exists() && snap.data()?.onboardedAt != null;
    if (onboarded) writeOnboardedCache(uid); // 次回以降はキャッシュで即決。
    return onboarded;
  } catch {
    // Firestore 読取失敗 → キャッシュ（既に false）にフォールバック・落とさない。
    return false;
  }
}

/**
 * ガイドを完了/スキップした（既読）として記録する。
 *   • Firestore `userPrefs/{uid}` に onboardedAt をマージ書き込み（本ソース）。
 *   • localStorage にもキャッシュ（同一端末の次回ちらつき防止）。
 * db 未設定時は localStorage のみ（従来挙動）。いずれも失敗は握りつぶす。
 */
export async function markOnboarded(uid: string): Promise<void> {
  if (!uid) return;
  // 同一端末のちらつき防止キャッシュは Firestore の成否に関わらず先に書く。
  writeOnboardedCache(uid);
  if (!db) return; // DEV/未構成は localStorage だけで完結。
  try {
    await setDoc(doc(db, 'userPrefs', uid), { onboardedAt: serverTimestamp() }, { merge: true });
  } catch {
    /* Firestore 書込失敗 → localStorage キャッシュは残るので同一端末では既読扱い・安全側 */
  }
}
